import React, {
  useContext,
  useCallback,
  useState,
  useEffect,
  useMemo,
} from "react";
import { AuthContext } from "context/AuthContext";
import { OrganizationContext } from "context/OrganizationContext";
import { showToast } from "ui/components/common";
import Search from "ui/search";
import OrganizationItem from "ui/organization/organization-item";
import { getOrgUser } from "lib/actions";
import { throttle } from "lodash";
import { useLocation, useSearchParams } from "react-router-dom";
import RoleSelectPopover from "ui/components/role-select-popover";
import PubSub from "pubsub-js";
import { USER_ROLE } from "lib/constants";
import { date } from "zod";

type OrgUserType = {
  id: string;
  email: string;
  accountName: string;
  orgId: string;
  role: string;
  token: string;
  name: string;
  phoneNumber: string;
  firstName: string;
  lastName: string;
};
const PageSize = 20;
export default function OrganizationList() {
  const { user } = useContext(AuthContext);
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { setSelectedUser, selectedUser } = useContext(OrganizationContext);

  const [scrollOffset, setScrollOffset] = useState(null);

  const [offset, setOffset] = useState(0);

  const [total, setTotal] = useState(0);
  const [userList, setUserList] = useState<{ user: OrgUserType }[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [roles, setRoles] = useState([]);
  const [random, setRandom] = useState(0);

  const queryName = useMemo(() => {
    const params = new URLSearchParams(searchParams);
    console.log("queryString", params.toString());
    return params?.get("query") || "";
  }, [location]);

  useEffect(() => {
    updateShowFetch(Date.now());
  }, [queryName, roles]);

  useEffect(() => {
    setUserList([]);
    fetchOrgList(user.orgId, queryName || "", 0);
  }, [random]);

  useEffect(() => {
    const handler = PubSub.subscribe("update-user-org", () => {
      updateShowFetch(Date.now());
    });
    return () => {
      PubSub.unsubscribe(handler);
    };
  }, []);

  const updateShowFetch = (random) => {
    setRandom(random);
  };

  useEffect(() => {
    console.log("location changed", location);

    setUserList([]);
  }, [location.pathname, location.search]);

  useEffect(() => {
    console.log(
      "scrollOffset",
      scrollOffset,
      total,

      isLoading,
    );
    if (scrollOffset < 5 && total > (offset + 1) * PageSize && !isLoading) {
      handleLoadMore();
    }
  }, [scrollOffset]);

  const handleLoadMore = () => {
    fetchOrgList(user.orgId, queryName, offset + 1);
  };

  const fetchOrgList = async (
    orgId: string,
    name: string,
    offset: number = 0,
  ) => {
    try {
      setIsLoading(true);

      const res = await getOrgUser(
        user.token,
        orgId,
        name,
        roles.join(","),
        offset,
        PageSize,
      );

      if (res.status !== 200) {
        showToast(1, "Failed to fetch case list. Please try again later.");
        setIsLoading(false);
        return;
      }

      const data = res?.data as {
        data: { user: OrgUserType }[];
        page: { offset: number; total: number };
      };
      if (offset == 0) {
        setUserList(data.data);
      } else {
        setUserList((prev) => [...prev, ...data.data]);
      }

      setOffset(offset);
      setTotal(res?.data?.page?.total);
    } catch (error) {
      console.error("Failed to fetch case list", error);
      showToast(1, "Failed to fetch case list. Please try again later.");
    }

    setIsLoading(false);
  };

  const onScroll = useCallback(
    throttle((e) => {
      const { scrollTop, scrollHeight, clientHeight } = e.target;
      setScrollOffset(scrollHeight - scrollTop - clientHeight);
    }, 200),
    [],
  );

  const handleAddUser = () => {
    setSelectedUser(null);
  };

  const handleSelectUser = (orgUser) => {
    setSelectedUser(orgUser);
  };

  let availableRoles = [];
  switch (user?.role) {
    case USER_ROLE.Dealer:
      availableRoles = [
        USER_ROLE.DealerAdmin,
        USER_ROLE.DealerRep,
        USER_ROLE.Technician,
        USER_ROLE.ProjectManager,
      ];
      if (user?.organization?.enableMonitoring) {
        availableRoles = availableRoles.concat([
          USER_ROLE.Inspector,
          USER_ROLE.Dispatcher,
          USER_ROLE.Operator,
          USER_ROLE.Scheduler,
        ]);
      }
      break;
    case USER_ROLE.DealerAdmin:
      availableRoles = [
        USER_ROLE.DealerRep,
        USER_ROLE.Technician,
        USER_ROLE.ProjectManager,
      ];
      if (user?.organization?.enableMonitoring) {
        availableRoles = availableRoles.concat([
          USER_ROLE.Inspector,
          USER_ROLE.Dispatcher,
          USER_ROLE.Operator,
          USER_ROLE.Scheduler,
        ]);
      }
      break;
    case USER_ROLE.Customer:
    case USER_ROLE.OrgAdmin:
    case USER_ROLE.ProjectManager:
      availableRoles = [
        USER_ROLE.OrgAdmin,
        USER_ROLE.SiteManager,
        USER_ROLE.ContactUser,
      ];
      break;
    default:
      break;
  }

  return (
    <div className="w-full h-full">
      <div className="flex items-center justify-between gap-2 pb-2 mx-6">
        <Search
          placeholder="Search by name or email..."
          className="relative flex flex-1 flex-shrink-0 h-[46px]"
        />
        <button
          onClick={handleAddUser}
          className="flex items-center justify-center w-10 h-10 text-white bg-blue-500 rounded-md text-2xl"
          aria-label="Add New User"
        >
          +
        </button>
      </div>
      <div className="mx-6 my-2">
        <RoleSelectPopover
          selectedRoles={roles}
          onRolesChange={(roles) => setRoles(roles)}
          availableRoles={availableRoles}
          multiSelect={false}
        />
      </div>

      <div className="w-full overflow-y-auto h-full pb-6" onScroll={onScroll}>
        {userList?.map((orgUser) => (
          <OrganizationItem
            key={orgUser.user?.id}
            orgUser={orgUser}
            onSelect={handleSelectUser}
            selectedUser={selectedUser}
          />
        ))}
        {!isLoading && userList?.length == 0 && (
          <p className="text-sm text-center text-neutral-500 pb-3 pt-1">
            No matching users
          </p>
        )}
        {!isLoading && total <= (offset + 1) * PageSize && offset > 2 && (
          <p className="text-sm text-center text-neutral-500 pb-3 pt-1">
            No More user
          </p>
        )}
      </div>
    </div>
  );
}
